import {
  Address,
  BundleId,
  BundleIdSchema,
  TicketEventId,
  TicketEventSchema,
} from "@greeter/core";
import {
  DateOnlySchema,
  TimeOfDay,
  TimeOfDayPeriodSchema,
} from "@greeter/date";
import { dedent } from "@greeter/formatters";
import { Log } from "@greeter/log";
import { isType } from "@greeter/util";
import { IVenueApi } from "@greeter/venue-api";
import {
  createFetchMachine,
  FetchActorFields,
  SetterEvents,
} from "@greeter/xstate-util";
import { v7 } from "uuid";
import { assign, setup } from "xstate";
import z from "zod";
import { $ZodErrorTree } from "zod/v4/core";

export type EditTicketEventApiRequirements = Pick<
  IVenueApi,
  | "saveTicketEvent"
  | "fetchTicketEvent"
  | "uploadAsset"
  | "fetchVenue"
  | "fetchBundles"
>;

export const TicketEventPeriod = z.object({
  date: z.date(),
  timePeriod: TimeOfDayPeriodSchema,
});

export const TicketEventPublishingStatusSchema = z.union([
  z.literal("published"),
  z.literal("draft"),
]);

export const TicketEventFormSchema = z.object({
  id: TicketEventSchema.shape.id,
  title: TicketEventSchema.shape.title.min(1, { error: "Mangler en titel" }),
  publishingStatus: TicketEventPublishingStatusSchema,
  slug: TicketEventSchema.shape.slug,
  description: TicketEventSchema.shape.description.min(1, {
    error: "Mangler en beskrivelse",
  }),
  coverAssets: TicketEventSchema.shape.coverAssets.min(1, {
    error: "Mangler et billede",
  }),
  eventPeriod: TicketEventSchema.shape.eventPeriod,
  address: TicketEventSchema.shape.address.min(1, {
    error: "Der skal være en adresse tilknyttet",
  }),

  salesPeriod: TicketEventSchema.shape.salesPeriod,

  tickets: TicketEventSchema.shape.tickets
    .refine(
      (t) =>
        (isType(t.quantity, "limited") && t.quantity.quantity > 0) ||
        isType(t.quantity, "infinite"),
      { error: 'Kvantitet skal enten være "uendelig" eller over 1' }
    )
    .refine(
      (t) => {
        return (isType(t, "paid-single") && t.price > 0) || isType(t, "free");
      },
      { error: "Prisen skal være over 0 kr." }
    ),

  bundles: z.array(BundleIdSchema),
});

export type TicketEventForm = z.infer<typeof TicketEventFormSchema>;

type TicketEventEvents =
  | SetterEvents<TicketEventForm, "set.form">
  | { type: "set.form.bundles.toggle"; data: BundleId }
  | SetterEvents<Pick<EditTicketEventContext, "form">, "set">
  | { type: "save" }
  | {
      type: "upload";
      data: Parameters<EditTicketEventApiRequirements["uploadAsset"]>;
    }
  | { type: "reset.address" }
  | { type: "validate" }
  | { type: "touch"; data: keyof TicketEventForm };

export type EditTicketEventContext = {
  form: TicketEventForm;
  formErrors?: $ZodErrorTree<TicketEventForm>;
  touched: Set<keyof TicketEventForm>;
  api: FetchActorFields<EditTicketEventApiRequirements>;
};
export const EditTicketEventMachine = setup({
  types: {} as {
    context: EditTicketEventContext;
    events: TicketEventEvents;
    input: { id?: string; api: EditTicketEventApiRequirements };
  },
}).createMachine({
  context({ spawn, input, self }) {
    const fetchVenueRef = spawn(
      createFetchMachine(input.api.fetchVenue.bind(input.api)),
      { syncSnapshot: true }
    );
    fetchVenueRef.on("fetched", (d) => {
      self.send({
        type: "set.form.address",
        data: Address.toString(d.data.address),
      });
    });
    fetchVenueRef.send({ type: "fetch", data: [] });

    const fetchBundlesRef = spawn(
      createFetchMachine(input.api.fetchBundles.bind(input.api))
    );
    fetchBundlesRef.send({ type: "fetch", data: [] });

    const saveTicketEventRef = spawn(
      createFetchMachine(input.api.saveTicketEvent.bind(input.api)),
      { syncSnapshot: true }
    );
    saveTicketEventRef.on("fetched", (d) => {
      Log.info("[EditTicketEventMachine][saveTicketEventRef]", {
        ticketEvent: d,
      });
      self.send({ type: "set.form", data: d.data });
    });
    saveTicketEventRef.on("error", (d) => {
      Log.error("[EditTicketEventMachine][saveTicketEventRef] ERROR", { d });
    });

    const fetchTicketEventRef = spawn(
      createFetchMachine(input.api.fetchTicketEvent.bind(input.api)),
      { syncSnapshot: true }
    );
    Log.debug("[EditTicketEventMachine][fetchTicketEventRef]", {
      input,
    });
    if (input.id) {
      fetchTicketEventRef.on("error", (result) =>
        Log.error("[EditTicketEventMachine][fetchTicketEventRef][saved]", {
          result,
          input,
        })
      );
      fetchTicketEventRef.on("fetched", (d) => {
        Log.debug("[EditTicketEventMachine][fetchTicketEventRef][fetched]", d);
        self.send({
          type: "set.form",
          data: d.data,
        });
      });
      fetchTicketEventRef.send({ type: "fetch", data: [{ id: input.id }] });
    }

    const uploadAssetRef = spawn(
      createFetchMachine(input.api.uploadAsset.bind(input.api))
    );
    uploadAssetRef.on("fetched", (d) => {
      self.send({ type: "set.form.coverAssets", data: [d.data] });
    });

    return {
      touched: new Set(),
      api: {
        fetchTicketEventRef,
        saveTicketEventRef,
        uploadAssetRef,
        fetchVenueRef,
        fetchBundlesRef,
      },
      form: {
        id: v7() as TicketEventId,
        title: "",
        slug: "",
        address: "",
        publishingStatus: "draft",
        description: dedent`
          ## Om eventet
          Weekenden topper i aften, når vi endnu engang åbner dørene til en ægte lørdagsklassiker i alpehytten!

          ## Programmet
          22:00 – Dørene åbne
          22:00 – 23:00 – Halv pris i baren
          23:00 – 00:00 – Spot-tilbud
          00:00 – Festen fortsætter hele natten
          `,
        eventPeriod: {
          type: "once",
          date: DateOnlySchema.parse(new Date()),
          timePeriod: {
            from: TimeOfDay.parse("16:00"),
            to: TimeOfDay.parse("04:00"),
          },
        },
        coverAssets: [],
        tickets: {
          type: "paid-single",
          quantity: {
            type: "infinite",
          },
          price: 0,
        },
        bundles: [],
      },
    };
  },

  on: {
    validate: {
      actions: assign({
        formErrors: ({ context }) => {
          const parsed = TicketEventFormSchema.safeParse(context.form);
          if (!parsed.error) return undefined;
          const errors = z.treeifyError(parsed.error);
          return errors;
        },
      }),
    },
    touch: {
      actions: assign({
        touched: ({ context, event }) => {
          const n = new Set(context.touched);
          n.add(event.data);
          return n;
        },
      }),
    },
    save: {
      actions: ({
        context: {
          form,
          api: { saveTicketEventRef },
        },
      }) => {
        saveTicketEventRef.send({ type: "fetch", data: [form] });
      },
    },
    "reset.address": {
      actions: ({
        context: {
          api: { fetchVenueRef },
        },
      }) => fetchVenueRef.send({ type: "fetch", data: [] }),
    },
    upload: {
      actions: ({
        context: {
          api: { uploadAssetRef },
        },
        event,
      }) => {
        uploadAssetRef.send({ type: "fetch", data: event.data });
      },
    },
    "set.form": {
      actions: assign(({ event }) => {
        return { form: event.data };
      }),
    },
    "set.form.*": {
      actions: assign(({ context, event }) => {
        switch (event.type) {
          case "set.form.bundles.toggle":
            const bundles = new Set(context.form.bundles);
            if (bundles.has(event.data)) {
              bundles.delete(event.data);
            } else {
              bundles.add(event.data);
            }
            return {
              form: { ...context.form, bundles: Array.from(bundles) },
            };
          case "set.form.bundles":
            return { form: { ...context.form, bundles: event.data } };
          default:
            // TODO: this should probably be its own function
            const field = event.type.split(".").at(-1) as keyof TicketEventForm;
            const f: EditTicketEventContext["form"] = {
              ...context.form,
            };
            if (field) f[field] = event.data as any;

            return { form: f };
        }
      }),
    },
  },
});
